// MODULES
import React from "react";
import PropTypes from "prop-types";

// CLASSES
import Session from "@classes/Session";

// COMPONENTS
import DefaultFields from "../../components/DefaultFields/DefaultFields";
import EditableField from "../../components/EditableField/EditableField";
import FieldsCollection from "@components/FieldsCollection/FieldsCollection";
import Button from "@components/Button/Button";
import StackLayout from "@components/layouts/StackLayout/StackLayout";
import Select from "@components/Select/Select";

// HELPERS
import {createClassName, loop} from "@helpers/utils";
import * as CobHelpers from "../../Cob.helpers";
import fulfillmentCheck from "./Poa.fulfillment";
import countries from "@helpers/countries";
import Validator from "../../../../classes/Validator";
import Checkbox from "../../../../components/Checkbox/Checkbox";
import Slider from "../../../../components/Slider/Slider";

// Partner specific config
import poaConfig from "./PoaConfig";

const countriesOptions = countries.map(({label, code}) => ({label, value: code}));
const countriesOptionsValuesOnly = countries.map(({label, code}) => code);


export default class CobPoa extends React.PureComponent {

	static propTypes = {
		id: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		session: PropTypes.instanceOf(Session).isRequired,
		sectionNumber: PropTypes.number.isRequired,
		number: PropTypes.number.isRequired,
		prevState: PropTypes.object,
		onChange: PropTypes.func.isRequired
	};

	static companyExclusive = true;
	static privateExclusive = false;
	static fields = _getFields();

	state = _getInitialState.call(this);

	componentDidUpdate(prevProps, prevState) {
		const {props, state} = this;
		const {id, onChange} = props;

		if (prevState.fields !== state.fields || prevState.numberOfCompanyReps !== state.numberOfCompanyReps) {
			onChange(id, {...state}, fulfillmentCheck(props, state));
		}
	}

	render() {

		const {props, state} = this;
		const {title, partner} = props;

		const classes = createClassName("Cob-Poa", {
			"Cob-View": true,
			"fulfilled": state.isFulfilled
		});

		const companyRepsList = [];

		let hasAtLeastOneSignatory = false;
		let hasAtLeastOneForvaltarFullmakt = false;

		for (let i = 0; i < this.state.numberOfCompanyReps; i++) {
			const selectedCitizenIndex = (() => {
				let returnValue = countriesOptionsValuesOnly.indexOf(this.state.fields["company_representative_citizenship_" + i]);
				if (returnValue === -1) {
					returnValue = countriesOptionsValuesOnly.indexOf("SE");
				} // If -1, set "SE" as default
				return returnValue;
			})();

			if (state.fields["checkbox_signatory_" + i] === "yes" || state.fields["checkbox_poa_" + i] === "yes") {
				hasAtLeastOneSignatory = true;
			}

			if (state.fields["checkbox_mng_" + i] === "yes") {
				hasAtLeastOneForvaltarFullmakt = true;
			}

			let forvaltarFullmaktLabel = "Förvaltarfullmakt";
			if (poaConfig[partner.name] && poaConfig[partner.name].showLongLabelForvaltarfullmakt) {
				forvaltarFullmaktLabel = "Förvaltarfullmakt (den privatperson som ska ha rätt att handla på depån)"
			}

			companyRepsList.push(
				<div key={i} className="rep">
					<h4>Företrädare {i + 1}</h4>

					<div className="rep-type">
						<Checkbox
							name={"checkbox_signatory_" + i}
							label="Firmatecknare"
							value="yes"
							checked={state.fields["checkbox_signatory_" + i] === "yes"}
							onChange={this._onTypeChange.bind(this, "checkbox_signatory_" + i)}
						/>
						<Checkbox
							name={"checkbox_ubo_" + i}
							label="Verklig huvudman"
							value="yes"
							checked={state.fields["checkbox_ubo_" + i] === "yes"}
							onChange={this._onTypeChange.bind(this, "checkbox_ubo_" + i)}
						/>

						{poaConfig[partner.name] && poaConfig[partner.name].showFullmakt && (
							<Checkbox
								name={"checkbox_poa_" + i}
								label="Fullmakt"
								value="yes"
								checked={state.fields["checkbox_poa_" + i] === "yes"}
								onChange={this._onTypeChange.bind(this, "checkbox_poa_" + i)}
							/>
						)}

						{poaConfig[partner.name] && poaConfig[partner.name].showInsynsfullmakt && (
							<Checkbox
								name={"checkbox_view_" + i}
								label="Insynsfullmakt"
								value="yes"
								checked={state.fields["checkbox_view_" + i] === "yes"}
								onChange={this._onTypeChange.bind(this, "checkbox_view_" + i)}
							/>
						)}

						{poaConfig[partner.name] && poaConfig[partner.name].showForvaltarfullmakt && (
							<Checkbox
								name={"checkbox_mng_" + i}
								label={forvaltarFullmaktLabel}
								value="yes"
								checked={state.fields["checkbox_mng_" + i] === "yes"}
								onChange={this._onTypeChange.bind(this, "checkbox_mng_" + i)}
							/>
						)}
					</div>

					<FieldsCollection
						fieldsPerRow={2}
						fields={[
							{
								render: (
									<EditableField
										name={"company_representative_firstname_" + i}
										label="Förnamn"
										value={state.fields["company_representative_firstname_" + i]}
										valid={state.validFields["company_representative_firstname_" + i]}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								)
							}, {
								render: (
									<EditableField
										name={"company_representative_lastname_" + i}
										label="Efternamn"
										value={state.fields["company_representative_lastname_" + i]}
										valid={state.validFields["company_representative_lastname_" + i]}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								)
							}, {
								render: (
									<EditableField
										name={"company_representative_sec_num_" + i}
										label="Personnummer"
										placeholder="ÅÅÅÅMMDD-XXXX"
										value={state.fields["company_representative_sec_num_" + i]}
										valid={state.validFields["company_representative_sec_num_" + i]}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								)
							}, {
								render: (
									<EditableField
										type="email"
										name={"company_representative_email_" + i}
										label="E-postadress"
										value={state.fields["company_representative_email_" + i]}
										valid={state.validFields["company_representative_email_" + i]}
										onChange={this._onFieldChange}
										onBlur={this._onFieldBlur}
										required
									/>
								)
							}, {
								render: (
									<Select
										name={"company_representative_citizenship_" + i}
										label="Nationalitet"
										options={countriesOptions}
										selectedIndex={selectedCitizenIndex}
										onChange={this._onCitizenshipChange.bind(this, "company_representative_citizenship_" + i)}
									/>
								)
							}, {
								display: state.fields["checkbox_ubo_" + i] === "yes",
								render: (
									<React.Fragment>
										<Slider
											label="Ägarandel"
											value={parseInt(state.fields["company_representative_percentage_" + i] || "0", 10)}
											min="25"
											unit="%"
											visibleValues={[0, 25, 50, 75, 100]}
											onChange={this._onOwnershipChange.bind(this, "company_representative_percentage_" + i)}
										/>
										<input
											type="hidden"
											name={`company_representative_percentage_${i}`}
											value={parseInt(state.fields["company_representative_percentage_" + i] || "0", 10) + "%"}
										/>
									</React.Fragment>
								)
							}
						]}
					/>

					{poaConfig[partner.name] && poaConfig[partner.name].showAttachFullmaktWarning && ((poaConfig[partner.name].showFullmakt && state.fields["checkbox_poa_" + i] === "yes") || (!poaConfig[partner.name].showFullmakt && state.fields["checkbox_mng_" + i] === "yes")) && (
						<div className="Notice warning" style={{marginTop: "20px"}}>
							Var god notera att fullmakten måste bifogas som PDF under "Övriga dokument" nedan.
						</div>
					)}
				</div>
			);
		}

		return (
			<div className={classes}>
				<DefaultFields fields={this.constructor.fields}/>

				<div className="Cob-Fields">
					<h2>{title}</h2>

					<p style={{margin: "0 0 20px 0"}}>
						Om det finns en verklig huvudman (som äger mer än 25 %) måste denna/dessa anges.
					</p>

					{(!hasAtLeastOneSignatory || (state.forceAtLeastOneForvaltarfullmakt && !hasAtLeastOneForvaltarFullmakt)) && (
						<div className="Notice warning" style={{margin: "0 0 20px 0"}}>
							<ul style={{width: "100%", padding: "0", margin: "0"}}>
								{!hasAtLeastOneSignatory && (
									<li style={{alignItems: "flex-start", width: "100%", padding: "10px 0"}}>
										{state.showFullmakt && (
											<React.Fragment>
												Åtminstone en firmatecknare/fullmaktshavare måste anges.
											</React.Fragment>
										)}

										{!state.showFullmakt && (
											<React.Fragment>
												Åtminstone en firmatecknare måste anges.
											</React.Fragment>
										)}
									</li>
								)}

								{state.forceAtLeastOneForvaltarfullmakt && !hasAtLeastOneForvaltarFullmakt && (
									<li style={{alignItems: "flex-start", width: "100%", padding: "10px 0"}}>
										Åtminstone en förvaltarfullmaktshavare måste anges.
									</li>
								)}
							</ul>
						</div>
					)}

					{companyRepsList}

					{this.state.numberOfCompanyReps <= 6 && (
						<StackLayout fullWidth>
							<Button
								size="small"
								label="Ta bort sista"
								appearance={this.state.numberOfCompanyReps <= 1 ? "medium" : "danger"}
								disabled={this.state.numberOfCompanyReps <= 1}
								onClick={() => this.setState({numberOfCompanyReps: this.state.numberOfCompanyReps - 1})}
							/>

							<Button
								size="small"
								label="Lägg till"
								appearance={this.state.numberOfCompanyReps >= 6 ? "medium" : "primary"}
								disabled={this.state.numberOfCompanyReps >= 6}
								onClick={() => this.setState({numberOfCompanyReps: this.state.numberOfCompanyReps + 1})}
								filled
							/>
						</StackLayout>
					)}
				</div>
			</div>
		);
	}

	_onOwnershipChange = (name, value) => {
		const newStateProps = {
			fields: {...this.state.fields, [name]: value}
		};

		this.setState(newStateProps);
	};

	_onTypeChange = (name, value) => {
		const newStateProps = {
			fields: {...this.state.fields, [name]: value ? "yes" : ""}
		};

		this.setState(newStateProps);
	};

	_onCitizenshipChange = (name, value) => {
		const newStateProps = {
			fields: {...this.state.fields, [name]: value.value}
		};

		this.setState(newStateProps);
	};

	// Internal methods
	_fieldValidator = (name, value) => {
		if (name.indexOf("company_representative_email_") > -1) {
			return Validator.validateEmail(value);
		}

		if (name.indexOf("company_representative_sec_num_") > -1) {
			return Validator.validateSsn(value, false);
		}

		return value.length > 0;
	};

	_onFieldChange = (name, value) => {
		CobHelpers.onViewFieldChange.call(this, name, value);
	};

	_onFieldBlur = (name) => {
		CobHelpers.onViewFieldBlur.call(this, name);
	};
}


// PRIVATE FUNCTIONS
function _getFields() {
	return [
		// Up to 6 company reps supported on the form
		...(() => {
			const returnValue = [];
			loop(6, i => {
				returnValue.push("checkbox_signatory_" + i);
				returnValue.push("checkbox_ubo_" + i);
				returnValue.push("checkbox_poa_" + i);
				returnValue.push("checkbox_mng_" + i);
				returnValue.push("checkbox_view_" + i);
				returnValue.push("company_representative_firstname_" + i);
				returnValue.push("company_representative_lastname_" + i);
				returnValue.push("company_representative_citizenship_" + i);
				returnValue.push("company_representative_sec_num_" + i);
				returnValue.push("company_representative_email_" + i);
				returnValue.push("company_representative_percentage_" + i);
			});
			return returnValue;
		})()
	];
}

function _getInitialState() {
	if (this.props.prevState) {
		return {...this.props.prevState};
	}

	const initialFieldsState = {};
	_getFields().forEach(function (field) {
		if (field.indexOf("company_representative_citizenship_") > -1) {
			initialFieldsState[field] = "SE";
		}
		else {
			initialFieldsState[field] = "";
		}
	});

	let initialState = {
		numberOfCompanyReps: 1,
		validFields: {},
		fields: initialFieldsState
	};

	initialState.forceAtLeastOneForvaltarfullmakt = poaConfig[this.props.partner.name] && poaConfig[this.props.partner.name].forceAtLeastOneForvaltarfullmakt;
	return initialState;
}


// WEBPACK FOOTER //
// ./src/routes/Cob/views/Poa/Poa.js